/**
 * OkbotAdminPanel.jsx
 * Super-admin panel for OkBot/OkBook:
 *  — API keys for audiobook sources & TTS providers
 *  — OkBook configuration (basic + advanced)
 *  — Usage statistics
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Key, Settings, BarChart3, RefreshCw, Plus, Trash2, Check,
  AlertCircle, ExternalLink, Eye, EyeOff, Save, ChevronDown, ChevronUp,
  Headphones, Globe, Zap, Music, Archive, BookMarked, Activity,
  ToggleLeft, ToggleRight, Shield, Info, Copy, Play
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ── Tiny helpers ──────────────────────────────────────────────────────────────
const fmt = (n, d = 2) => Number(n || 0).toFixed(d);
const fmtNum = (n) => Number(n || 0).toLocaleString();

const CATEGORY_ICONS = {
  tts: Headphones,
  audiobooks: BookOpen,
};
const CATEGORY_LABELS = { tts: 'Text-to-Speech', audiobooks: 'Fuentes de Audiolibros' };

// ── Service card ──────────────────────────────────────────────────────────────
function ServiceCard({ svc, onSave, onDelete, onTest }) {
  const [editing, setEditing] = useState(false);
  const [keyVal, setKeyVal] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [notes, setNotes] = useState(svc.notes || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const Icon = CATEGORY_ICONS[svc.category] || Key;

  const handleSave = async () => {
    if (!keyVal.trim()) return;
    setSaving(true);
    try {
      await onSave({ service: svc.id, api_key: keyVal, notes, enabled: true });
      setEditing(false);
      setKeyVal('');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try { const r = await onTest(svc.id); setTestResult(r); }
    finally { setTesting(false); }
  };

  return (
    <div className={`glass-panel p-5 rounded-2xl border transition-all ${
      svc.configured ? 'border-green-500/20' : 'border-white/5'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          svc.configured ? 'bg-green-500/15 border border-green-500/25' : 'bg-white/5 border border-white/10'
        }`}>
          <Icon size={18} className={svc.configured ? 'text-green-400' : 'text-gray-500'} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="text-white font-semibold text-sm">{svc.name}</h3>
            {svc.no_key_required && (
              <span className="text-xs bg-blue-500/15 text-blue-400 border border-blue-500/25 px-2 py-0.5 rounded-full">Sin clave</span>
            )}
            {svc.free_tier && !svc.no_key_required && (
              <span className="text-xs bg-purple-500/15 text-purple-400 border border-purple-500/25 px-2 py-0.5 rounded-full">Free tier</span>
            )}
            {svc.configured && (
              <span className="flex items-center gap-1 text-xs bg-green-500/15 text-green-400 border border-green-500/25 px-2 py-0.5 rounded-full">
                <Check size={10} /> Configurada
              </span>
            )}
          </div>
          <p className="text-gray-500 text-xs mb-3 leading-relaxed">{svc.description}</p>

          {/* Configured key display */}
          {svc.configured && !editing && (
            <div className="flex items-center gap-2 mb-3">
              <code className="text-xs bg-white/5 text-orange-400 px-3 py-1.5 rounded-lg border border-white/10">
                {svc.masked_key}
              </code>
              {svc.notes && <span className="text-xs text-gray-600 italic">"{svc.notes}"</span>}
            </div>
          )}

          {/* Edit form */}
          {editing && !svc.no_key_required && (
            <div className="space-y-2 mb-3">
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={keyVal}
                  onChange={e => setKeyVal(e.target.value)}
                  placeholder={`Clave API (${svc.key_pattern || 'ej. sk-...'})`}
                  className="w-full pr-10 pl-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50 font-mono"
                />
                <button onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              <input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notas (opcional)"
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:border-violet-500/50"
              />
            </div>
          )}

          {/* Test result */}
          {testResult && (
            <div className={`text-xs p-2.5 rounded-lg mb-3 ${
              testResult.success ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                                 : 'bg-red-500/10 text-red-400 border border-red-500/20'
            }`}>
              {testResult.success ? `✓ ${testResult.info}` : `✗ ${testResult.error}`}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            {svc.no_key_required ? (
              <div className="flex items-center gap-1.5 text-xs text-green-400">
                <Check size={12} /> API pública disponible sin clave
              </div>
            ) : editing ? (
              <>
                <button onClick={handleSave} disabled={saving || !keyVal.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-all">
                  {saving ? <RefreshCw size={12} className="animate-spin" /> : <Save size={12} />}
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
                <button onClick={() => { setEditing(false); setKeyVal(''); setTestResult(null); }}
                  className="px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-400 rounded-lg text-xs transition-all">
                  Cancelar
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { setEditing(true); setTestResult(null); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg text-xs border border-white/10 transition-all">
                  <Key size={12} /> {svc.configured ? 'Actualizar clave' : 'Agregar clave'}
                </button>
                {svc.configured && (
                  <button onClick={handleTest} disabled={testing}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/20 rounded-lg text-xs transition-all">
                    {testing ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                    Probar
                  </button>
                )}
                {svc.configured && (
                  <button onClick={() => onDelete(svc.id)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-lg text-xs transition-all">
                    <Trash2 size={12} /> Eliminar
                  </button>
                )}
              </>
            )}
            <a href={svc.docs_url || svc.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/10 text-gray-500 hover:text-gray-300 rounded-lg text-xs transition-all">
              <ExternalLink size={11} /> Docs
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Toggle switch ──────────────────────────────────────────────────────────────
function Toggle({ value, onChange, label }) {
  return (
    <button onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        value ? 'bg-violet-500' : 'bg-white/15'
      }`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        value ? 'translate-x-6' : 'translate-x-1'
      }`} />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function OkbotAdminPanel() {
  const [tab, setTab] = useState('keys');
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [config, setConfig] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Load services
  const loadServices = useCallback(async () => {
    setLoadingServices(true);
    try {
      const r = await axios.get(`${API}/admin/okbot/services`);
      setServices(r.data.services || []);
    } catch { toast.error('Error cargando servicios'); }
    finally { setLoadingServices(false); }
  }, []);

  // Load config
  const loadConfig = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/okbot/config`);
      setConfig(r.data);
    } catch { toast.error('Error cargando configuración'); }
  }, []);

  // Load stats
  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const r = await axios.get(`${API}/admin/okbot/stats`);
      setStats(r.data);
    } catch { toast.error('Error cargando estadísticas'); }
    finally { setLoadingStats(false); }
  }, []);

  useEffect(() => { loadServices(); }, [loadServices]);
  useEffect(() => { if (tab === 'config' && !config) loadConfig(); }, [tab, config, loadConfig]);
  useEffect(() => { if (tab === 'stats') loadStats(); }, [tab, loadStats]);

  const handleSaveKey = async (data) => {
    await axios.post(`${API}/admin/okbot/api-keys`, data);
    toast.success(`Clave guardada para ${data.service}`);
    loadServices();
  };

  const handleDeleteKey = async (service) => {
    if (!window.confirm(`¿Eliminar la clave de ${service}?`)) return;
    await axios.delete(`${API}/admin/okbot/api-keys/${service}`);
    toast.success('Clave eliminada');
    loadServices();
  };

  const handleTestKey = async (service) => {
    const r = await axios.post(`${API}/admin/okbot/test-key`, { service });
    return r.data;
  };

  const handleSaveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.post(`${API}/admin/okbot/config`, config);
      toast.success('Configuración guardada');
    } catch { toast.error('Error guardando configuración'); }
    finally { setSavingConfig(false); }
  };

  const updateConfig = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  // Group services by category
  const grouped = services.reduce((acc, s) => {
    const cat = s.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const TABS = [
    { id: 'keys', label: 'Claves API', icon: Key },
    { id: 'config', label: 'Config OkBook', icon: Settings },
    { id: 'stats', label: 'Estadísticas', icon: BarChart3 },
  ];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="glass-panel p-6 rounded-2xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500/30 to-purple-500/20 border border-violet-500/30 flex items-center justify-center">
            <BookOpen size={22} className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">OkBot · OkBook Control</h2>
            <p className="text-gray-500 text-sm">Fuentes de audiolibros, proveedores TTS y configuración avanzada</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                tab === t.id
                  ? 'bg-violet-500 text-white shadow-lg shadow-violet-500/20'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}>
              <Icon size={15} />{t.label}
            </button>
          );
        })}
      </div>

      {/* ── API KEYS TAB ───────────────────────────────────────────────────── */}
      {tab === 'keys' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">Gestiona las claves de acceso para TTS y fuentes de audiolibros.</p>
            <button onClick={loadServices} disabled={loadingServices}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 text-gray-400 rounded-xl text-sm hover:bg-white/10 transition-all">
              <RefreshCw size={14} className={loadingServices ? 'animate-spin' : ''} />
              Actualizar
            </button>
          </div>

          {loadingServices ? (
            <div className="flex justify-center py-16"><RefreshCw size={24} className="animate-spin text-violet-400" /></div>
          ) : (
            Object.entries(grouped).map(([cat, svcs]) => (
              <div key={cat}>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  {React.createElement(CATEGORY_ICONS[cat] || Key, { size: 13 })}
                  {CATEGORY_LABELS[cat] || cat}
                </h3>
                <div className="space-y-3">
                  {svcs.map(svc => (
                    <ServiceCard
                      key={svc.id}
                      svc={svc}
                      onSave={handleSaveKey}
                      onDelete={handleDeleteKey}
                      onTest={handleTestKey}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* ── CONFIG TAB ─────────────────────────────────────────────────────── */}
      {tab === 'config' && (
        <div className="space-y-5">
          {!config ? (
            <div className="flex justify-center py-16"><RefreshCw size={24} className="animate-spin text-violet-400" /></div>
          ) : (
            <>
              {/* Basic Config */}
              <div className="glass-panel p-6 rounded-2xl">
                <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
                  <Settings size={16} className="text-violet-400" />Configuración Básica
                </h3>
                <div className="space-y-5">
                  {/* Toggle options */}
                  {[
                    { key: 'show_continue_listening', label: 'Mostrar "Seguir escuchando"', desc: 'Sección de historial de reproducción en home' },
                    { key: 'auto_play_chapters', label: 'Reproducción automática de capítulos', desc: 'Pasar al siguiente capítulo automáticamente' },
                    { key: 'enable_download', label: 'Habilitar descarga de MP3', desc: 'Permitir a usuarios descargar narraciones generadas' },
                    { key: 'enable_librivox', label: 'Fuente: LibriVox', desc: '20,000+ audiolibros de dominio público' },
                    { key: 'enable_internet_archive', label: 'Fuente: Internet Archive', desc: 'Millones de audiolibros y grabaciones' },
                    { key: 'enable_gutenberg', label: 'Fuente: Project Gutenberg', desc: '70,000+ ebooks de dominio público' },
                  ].map(opt => (
                    <div key={opt.key} className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white text-sm font-medium">{opt.label}</p>
                        <p className="text-gray-600 text-xs mt-0.5">{opt.desc}</p>
                      </div>
                      <Toggle value={!!config[opt.key]} onChange={v => updateConfig(opt.key, v)} />
                    </div>
                  ))}

                  {/* Number inputs */}
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1.5">Resultados por página</label>
                      <input type="number" min={4} max={48} value={config.results_per_page || 12}
                        onChange={e => updateConfig('results_per_page', Number(e.target.value))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1.5">Velocidad por defecto</label>
                      <select value={config.default_playback_speed || 1.0}
                        onChange={e => updateConfig('default_playback_speed', Number(e.target.value))}
                        className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50">
                        {[0.75, 1.0, 1.25, 1.5, 1.75, 2.0].map(s =>
                          <option key={s} value={s}>{s}x</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Advanced Narration Config */}
              <div className="glass-panel p-6 rounded-2xl">
                <h3 className="text-white font-semibold mb-5 flex items-center gap-2">
                  <Headphones size={16} className="text-violet-400" />Narración con IA — Avanzado
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-1.5">Proveedor TTS principal</label>
                      <select value={config.narration_provider || 'fish_audio'}
                        onChange={e => updateConfig('narration_provider', e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50">
                        <option value="fish_audio">🐟 Fish Audio (recomendado)</option>
                        <option value="elevenlabs">ElevenLabs</option>
                        <option value="openai">OpenAI TTS</option>
                        <option value="browser">Browser (gratis, sin IA)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-1.5">Idioma de narración</label>
                      <select value={config.narration_language || 'auto'}
                        onChange={e => updateConfig('narration_language', e.target.value)}
                        className="w-full bg-[#111] border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50">
                        <option value="auto">Auto-detectar</option>
                        <option value="es">Español</option>
                        <option value="en">English</option>
                        <option value="pt">Português</option>
                        <option value="fr">Français</option>
                        <option value="de">Deutsch</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-1.5">Voice ID por defecto (opcional)</label>
                    <input value={config.narration_voice_id || ''}
                      onChange={e => updateConfig('narration_voice_id', e.target.value)}
                      placeholder="Deja vacío para usar voz por defecto del proveedor"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500/50" />
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-1.5">
                      Máximo de caracteres por narración
                      <span className="ml-2 text-gray-600">({config.max_narration_chars?.toLocaleString()} chars)</span>
                    </label>
                    <input type="range" min={500} max={8000} step={500}
                      value={config.max_narration_chars || 4000}
                      onChange={e => updateConfig('max_narration_chars', Number(e.target.value))}
                      className="w-full accent-violet-500" />
                    <div className="flex justify-between text-xs text-gray-600 mt-1">
                      <span>500</span><span>4,000</span><span>8,000</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-1.5">Notas internas</label>
                    <textarea rows={2} value={config.notes || ''}
                      onChange={e => updateConfig('notes', e.target.value)}
                      placeholder="Notas sobre la configuración…"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white text-sm resize-none focus:outline-none focus:border-violet-500/50" />
                  </div>
                </div>
              </div>

              {/* Save button */}
              <button onClick={handleSaveConfig} disabled={savingConfig}
                className="flex items-center gap-2 px-6 py-3 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-all">
                {savingConfig ? <RefreshCw size={15} className="animate-spin" /> : <Save size={15} />}
                {savingConfig ? 'Guardando…' : 'Guardar configuración'}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── STATS TAB ──────────────────────────────────────────────────────── */}
      {tab === 'stats' && (
        <div className="space-y-5">
          <div className="flex justify-end">
            <button onClick={loadStats} disabled={loadingStats}
              className="flex items-center gap-2 px-3 py-1.5 bg-white/5 text-gray-400 rounded-xl text-sm hover:bg-white/10 transition-all">
              <RefreshCw size={14} className={loadingStats ? 'animate-spin' : ''} /> Actualizar
            </button>
          </div>

          {loadingStats ? (
            <div className="flex justify-center py-16"><RefreshCw size={24} className="animate-spin text-violet-400" /></div>
          ) : stats?.success ? (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: 'Total narraciones', value: fmtNum(stats.narrations?.total), icon: Headphones, color: 'text-violet-400 bg-violet-500/10 border-violet-500/20' },
                  { label: 'Exitosas', value: fmtNum(stats.narrations?.successful), icon: Check, color: 'text-green-400 bg-green-500/10 border-green-500/20' },
                  { label: 'Caracteres totales', value: fmtNum(stats.narrations?.total_chars), icon: Activity, color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
                  { label: 'Costo estimado', value: `$${fmt(stats.narrations?.total_cost_usd, 4)}`, icon: Zap, color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20' },
                ].map(card => (
                  <div key={card.label} className={`rounded-2xl border p-5 ${card.color}`}>
                    <card.icon size={16} className="mb-3 opacity-70" />
                    <p className="text-2xl font-bold text-white">{card.value}</p>
                    <p className="text-xs mt-1 opacity-50">{card.label}</p>
                  </div>
                ))}
              </div>

              {stats.narrations?.total === 0 && (
                <div className="glass-panel p-12 rounded-2xl text-center">
                  <BarChart3 size={40} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">Sin datos de uso todavía</p>
                  <p className="text-gray-600 text-sm mt-1">Las estadísticas aparecerán cuando usuarios generen narraciones en OkBook</p>
                </div>
              )}
            </>
          ) : (
            <div className="glass-panel p-8 rounded-2xl text-center">
              <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
              <p className="text-gray-400">{stats?.error || 'Error cargando estadísticas'}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
